import React, { useId } from "react";
import { Card, CardHeader, CardLabel } from "./ui/Card";
import { TITLE_MAX } from "./broadcastEnums";
import { errorFor } from "./broadcastDraft";
import { EmailBodyEditor } from "./EmailBodyEditor";
import { DestinationChips } from "./EmailLinkPanel";

/**
 * The email half of the composer: a subject, a body, a button.
 *
 * ---------------------------------------------------------------------------
 * THREE FIELDS, NOT FIVE
 * ---------------------------------------------------------------------------
 * It used to also ask for a preview line and an image. Both are gone.
 *
 * The preview line was a second subject: authors wrote a paraphrase of the line
 * above it, and it existed mostly because the wire wants a `notification_body`.
 * That value is now derived from the body's own first words — which is what an
 * inbox preview line is anyway — so nobody has to write it twice. See
 * `serializeContent` in `broadcastPayload.js`.
 *
 * The image went because the email never showed it at a size worth uploading
 * for. The popup keeps its own; the two were always separate uploads.
 *
 * ---------------------------------------------------------------------------
 * WHY IT LOOKS DIFFERENT FROM THE POPUP CARD
 * ---------------------------------------------------------------------------
 * Deliberately, and not for variety. The two cards never appear together, so
 * the only way an author can tell at a glance which one they are looking at is
 * if the surfaces themselves differ. The popup card is warm — the console
 * accent, a rounded device-shaped preview. This one is ink and paper: a mono
 * envelope strip across the top, a hairline-ruled subject, a graphite focus
 * state and a body that sits on a tinted sheet.
 *
 * Purely presentational. Every value arrives as a prop, nothing here fetches,
 * and the only state it touches is the caller's via `onPatch`.
 */

/* ── Field chrome ─────────────────────────────────────────────────────── */

/**
 * Ink, not accent. The popup card's inputs focus to `orange-primary`; these
 * focus to `bc-text`, which is the one-line version of the whole visual split.
 */
const SUBJECT_INPUT =
  "mt-2 w-full bg-transparent text-[19px] font-medium tracking-[-0.015em] " +
  "text-bc-text placeholder:text-bc-faintest border-b border-bc-border pb-2 " +
  "focus:outline-none focus:border-bc-text transition-colors";

/**
 * The button pair, joined.
 *
 * One bordered box with a hairline down the middle rather than two inputs in a
 * grid, because the label and the link are not two settings — they are one
 * button, and validation already refuses half of one.
 */
const CTA_GROUP =
  "mt-2 flex items-stretch overflow-hidden rounded-[10px] border " +
  "border-bc-border bg-white focus-within:border-bc-text transition-colors";

const CTA_INPUT =
  "min-w-0 border-none bg-transparent px-3.5 py-2.5 text-sm text-bc-text " +
  "placeholder:text-bc-faintest focus:outline-none";

const HELPER_ROW = "mt-1.5 flex items-start justify-between gap-3";
const HELPER_TEXT = "text-xs text-bc-soft";

const SECTION = "mt-6";

/** The strip under the header: who the mail comes from, in the inbox's own font. */
const ENVELOPE_ROW =
  "mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-bc-neut-bg px-3 py-2 " +
  "font-mono text-[11px] text-bc-muted";

/* ── Pieces ───────────────────────────────────────────────────────────── */

/**
 * The live character counter, e.g. `21/60`.
 *
 * Turns red AT the limit rather than past it — `maxLength` stops typing at the
 * boundary, so "past" is a state the keyboard cannot reach.
 *
 * @param {Object} props
 * @param {number} props.length - Current character count.
 * @param {number} props.max - `TITLE_MAX`.
 * @param {string} props.testId - Stable hook for tests.
 * @returns {React.ReactElement}
 */
const Counter = ({ length, max, testId }) => (
  <span
    data-testid={testId}
    className={`shrink-0 font-mono tabular-nums text-xs ${
      length >= max ? "text-bc-bad" : "text-bc-faint"
    }`}
  >
    {length}/{max}
  </span>
);

/**
 * A field-level validation message.
 *
 * Renders nothing when there is no message, so the `aria-describedby` the field
 * points at simply does not resolve — correct, and quieter than an empty live
 * region.
 *
 * @param {Object} props
 * @param {string} props.id - Target of the field's `aria-describedby`.
 * @param {string} [props.message] - From `errorFor(errors, field)`.
 * @returns {React.ReactElement|null}
 */
const FieldError = ({ id, message }) =>
  message ? (
    <div id={id} role="alert" className="mt-1.5 text-xs text-bc-bad">
      {message}
    </div>
  ) : null;

/* ── The card ─────────────────────────────────────────────────────────── */

/**
 * Email content.
 *
 * Rendered only when the draft's mode is email — the composer decides that, not
 * this card.
 *
 * @param {Object} props
 * @param {import("./broadcastDraft").BroadcastDraft} props.draft - Reads `emailSubject`, `emailBodyHtml`, `emailCtaText`, `emailCtaUrl`. Reads no popup field.
 * @param {(partial: Object) => void} props.onPatch - Merges a partial into the draft. Never called with a whole draft.
 * @param {Array<{ field: string, message: string }>} [props.errors] - The array from `validateDraft`.
 * @param {string} [props.className] - Extra classes on the card, merged last.
 * @returns {React.ReactElement}
 */
export const EmailContentCard = ({
  draft,
  onPatch,
  errors,
  className = "",
}) => {
  const uid = useId();
  const subjectErrorId = `${uid}-subject-error`;
  const bodyErrorId = `${uid}-body-error`;
  const ctaTextErrorId = `${uid}-cta-text-error`;
  const ctaUrlErrorId = `${uid}-cta-url-error`;

  const subject = draft?.emailSubject || "";
  const bodyHtml = draft?.emailBodyHtml || "";
  const ctaText = draft?.emailCtaText || "";
  const ctaUrl = draft?.emailCtaUrl || "";

  const subjectError = errorFor(errors, "emailSubject");
  const bodyError = errorFor(errors, "emailBodyHtml");
  const ctaTextError = errorFor(errors, "emailCtaText");
  const ctaUrlError = errorFor(errors, "emailCtaUrl");

  const patch = (partial) => {
    if (onPatch) onPatch(partial);
  };

  return (
    <Card className={`p-5 ${className}`} as="section">
      <CardHeader
        title="Email content"
        subtitle="Written for the inbox, and only for the inbox."
        actions={
          <span className="rounded-full bg-bc-text px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-white">
            Email
          </span>
        }
      />

      {/* The envelope strip. Display only — the sender is fixed and the
          recipients are the audience picked below. It is here because it is
          what makes the card read as a piece of mail. */}
      <div className={ENVELOPE_ROW}>
        <span>From · Flex &lt;noreply.flexcellence.co&gt;</span>
        <span className="text-bc-faint">To · the audience below</span>
      </div>

      {/* 1 · Subject. */}
      <div className="mt-5">
        <CardLabel>Subject</CardLabel>
        <input
          type="text"
          value={subject}
          maxLength={TITLE_MAX}
          placeholder="Your March plan is ready"
          aria-label="Subject"
          aria-invalid={subjectError ? "true" : undefined}
          aria-describedby={subjectError ? subjectErrorId : undefined}
          onChange={(event) => patch({ emailSubject: event.target.value })}
          className={SUBJECT_INPUT}
        />
        <div className={HELPER_ROW}>
          <span className={HELPER_TEXT}>
            The line competing with everything else in the inbox.
          </span>
          <Counter
            length={subject.length}
            max={TITLE_MAX}
            testId="bc-email-subject-counter"
          />
        </div>
        <FieldError id={subjectErrorId} message={subjectError} />
      </div>

      {/* 2 · Body. Owns its own label row, because the Add link button lives
          there next to it. */}
      <div className={SECTION}>
        <EmailBodyEditor
          value={bodyHtml}
          onChange={(html) => patch({ emailBodyHtml: html })}
          invalid={Boolean(bodyError)}
          errorId={bodyError ? bodyErrorId : undefined}
        />
        <div className={HELPER_ROW}>
          <span className={HELPER_TEXT}>
            Select text to format it — with nothing selected, a size change
            resizes the whole email. Click a link to edit or remove it; links
            are rewritten server-side for click tracking.
          </span>
        </div>
        <FieldError id={bodyErrorId} message={bodyError} />
      </div>

      {/* 3 · Button — a pair, validated as a pair. This link is the email's
          only destination; the in-app screen picker is a popup control and is
          deliberately not offered here. */}
      <div className={SECTION}>
        <CardLabel>Button (optional)</CardLabel>
        <div className={CTA_GROUP}>
          <input
            type="text"
            value={ctaText}
            placeholder="Open Workouts"
            aria-label="Button label"
            aria-invalid={ctaTextError ? "true" : undefined}
            aria-describedby={ctaTextError ? ctaTextErrorId : undefined}
            onChange={(event) => patch({ emailCtaText: event.target.value })}
            className={`${CTA_INPUT} flex-1`}
          />
          <span aria-hidden="true" className="w-px shrink-0 bg-bc-border" />
          <input
            type="url"
            value={ctaUrl}
            placeholder="https://flex.fit/…"
            aria-label="Button link"
            aria-invalid={ctaUrlError ? "true" : undefined}
            aria-describedby={ctaUrlError ? ctaUrlErrorId : undefined}
            onChange={(event) => patch({ emailCtaUrl: event.target.value })}
            className={`${CTA_INPUT} flex-[1.2] font-mono text-xs`}
          />
        </div>

        <DestinationChips
          className="mt-2.5"
          onPick={(destination) =>
            patch({
              emailCtaUrl: destination.url,
              // Only names the button if it has no name yet — clobbering copy
              // the author wrote is not a shortcut.
              ...(ctaText.trim() ? {} : { emailCtaText: destination.label }),
            })
          }
        />

        <div className={HELPER_ROW}>
          <span className={HELPER_TEXT}>
            Both or neither — a label with no link is a dead button.
          </span>
        </div>
        <FieldError id={ctaTextErrorId} message={ctaTextError} />
        <FieldError id={ctaUrlErrorId} message={ctaUrlError} />
      </div>
    </Card>
  );
};

export default EmailContentCard;
